// useFeed — mounts the whole feed machinery once, at the app root:
// TanStack Query bootstrap (/events), the WS subscription writing deltas
// into the jotai store, the settled strip (/markets/past), and
// re-bootstrap on reconnect/staleness.

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';
import {
  fetchBootstrap,
  fetchChart,
  fetchPastMarkets,
  fetchRecentPastMarkets,
} from './client';
import {
  applyBootstrap,
  applyEventRemoved,
  applyEventWithMarkets,
  applyMarketUpdate,
  hydrateHistory,
  setArchive,
  setSettled,
} from './store';
import { connectZenoWs } from './ws';

const SETTLED_WINDOW = 20; // keep the last N removed markets for the strip
const ARCHIVE_LIMIT = 50; // settled markets fetched from mongo once per load

export function useFeed(): void {
  const queryClient = useQueryClient();
  const store = getDefaultStore();
  const [settledIndexes, setSettledIndexes] = useState<number[]>([]);

  // REST bootstrap — refetched only on explicit invalidation (reconnect)
  const bootstrap = useQuery({
    queryKey: ['feed', 'bootstrap'],
    queryFn: fetchBootstrap,
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (bootstrap.data) {
      applyBootstrap(store, bootstrap.data);
    }
  }, [bootstrap.data, store]);

  // Settled strip — fetched for the indexes zeno told us got removed
  const settled = useQuery({
    queryKey: ['feed', 'settled', settledIndexes.join(',')],
    queryFn: () => fetchPastMarkets(settledIndexes),
    enabled: settledIndexes.length > 0,
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (settled.data) {
      setSettled(store, settled.data);
    }
  }, [settled.data, store]);

  // Archive — settled markets straight from mongo, independent of the live board
  const archive = useQuery({
    queryKey: ['feed', 'archive'],
    queryFn: () => fetchRecentPastMarkets(ARCHIVE_LIMIT),
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (archive.data) {
      setArchive(store, archive.data);
    }
  }, [archive.data, store]);

  // Sparkline hydration — one chart fetch per market once bootstrap lands;
  // live WS deltas keep rolling on top (hydrateHistory never clobbers).
  useEffect(() => {
    if (!bootstrap.data) return;
    const ids = [
      ...new Set(
        bootstrap.data.flatMap((e) => Object.keys(e.markets ?? {}).map(Number)),
      ),
    ];
    let dead = false;
    void (async () => {
      const charts = await Promise.allSettled(ids.map((id) => fetchChart(id)));
      if (dead) return;
      charts.forEach((res, i) => {
        if (res.status === 'fulfilled') {
          hydrateHistory(store, ids[i], res.value);
        }
      });
    })();
    return () => {
      dead = true;
    };
  }, [bootstrap.data, store]);

  // WS deltas + re-bootstrap on reconnect/staleness
  useEffect(
    () =>
      connectZenoWs({
        onMessage: (delta) => {
          switch (delta.channel) {
            case 'market:updated':
              applyMarketUpdate(store, delta.market);
              break;
            case 'event:update':
            case 'event:added':
              applyEventWithMarkets(store, delta.event);
              break;
            case 'event:removed':
              applyEventRemoved(store, delta.eventId, delta.marketIndexes);
              setSettledIndexes((prev) =>
                [...delta.marketIndexes, ...prev].slice(0, SETTLED_WINDOW),
              );
              break;
          }
        },
        onReconnect: () => {
          queryClient.invalidateQueries({ queryKey: ['feed', 'bootstrap'] });
        },
      }),
    [queryClient, store],
  );
}
